/*
 * Copyright (c) Nexign, JSC, 2022-2025.
 */
/**
 * Personal data button.
 *
 * @author Dmitriy Kvon
 * @author Yakov Gushchin
 * @author Nikolay Sviridov
 * @version 1.38.0
 * @since 1.17.0
 */

import { useCallback, useMemo, useRef } from 'react';

import { useSelector } from 'react-redux';

import { InstructionLink } from '@components/common';
import { ELinkType } from '@components/common/InstructionLink/types';
import { PagePanelButtonSelectStyled } from '@components/common/PageTitle/styled/PageTitleStyled';
import { TableFooterNameType } from '@components/Table/types/tableTypes';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import { TInstructionLink } from '@modules/internationalization/types/corporate';
import { IPersonalButtonTranslation } from '@modules/internationalization/types/pages/subscriberMobile';
import { AccessComponentKeyEnum, FeatureNameEnum } from '@modules/role/constants/guard/guardConstants';
import {
    tableBasketCountSelector,
    tableBasketParamsSelector,
    tableButtonsModeSelector,
    tableMetaButtonsSelector,
} from '@modules/table/selectors';
import { makeSelectFeatureEnabled } from '@pages/PageAuth/selectors/authSelectors';
import { stringFormatParams } from '@utils/stringFormat';

import { icons } from '../../../../assets';
import Select, { IOptionItemProps } from '../../../../components/common/Select/Select';
import useTableFooterDisabled from '../../../../components/common/TableFooter/hooks/useTableFooterDisabled';
import declination from '../../../../utils/declination';
import { API_SUBSCRIBER_TABLE_META } from '../../constants';
import { EPageSubscriberConstants } from '../../pageSubscriberConstants';

export interface ISubscriberMobilePersonalDataButton {
    onSelectHandler: (option: IOptionItemProps) => void;
    translation: IPersonalButtonTranslation;
}

const SubscriberMobilePersonalDataButton = ({
                                                onSelectHandler,
                                                translation,
                                            }: ISubscriberMobilePersonalDataButton) => {

    const translationCommon = useSelector(makeSelectTranslationCommon());

    const buttons = useSelector(tableMetaButtonsSelector(API_SUBSCRIBER_TABLE_META));
    const basketCount = useSelector(tableBasketCountSelector);
    const buttonsMode = useSelector(tableButtonsModeSelector);
    const basketParams = useSelector(tableBasketParamsSelector);
    const isImportSubscribersLimitsAvailable =
        useSelector(makeSelectFeatureEnabled(FeatureNameEnum.IMPORT_SUBSCRIBERS_LIMITS));
    const isInstructionsLinksEnabled = useSelector(makeSelectFeatureEnabled(FeatureNameEnum.INSTRUCTIONS_LINKS));
    const {
        instructionsLinks: {
            subsImportSubs,
            subsImportPers,
            subsImportPersDel,
            subsImportLimits,
        },
    } = translationCommon;

    const instructionLinkRef = useRef<HTMLAnchorElement | null>(null);

    const {
        isBtnDisabled,
        getMaxCount,
    } = useTableFooterDisabled(TableFooterNameType.subscriberMobile,
        buttons,
        buttonsMode,
        basketCount,
        basketParams,
    );

    const lowerLimitDisable = basketCount === 0;
    const upperLimitDisable = isBtnDisabled(AccessComponentKeyEnum.TitleButtonPersonalDataDelete);
    const maxCount = getMaxCount(AccessComponentKeyEnum.TitleButtonPersonalDataDelete);
    const declinedSubs = maxCount ? declination(maxCount, translation.declinedSubs) : '';
    const buttonLabel = lowerLimitDisable ? translation.disabledByLowerLimitBtn
        : upperLimitDisable ? stringFormatParams(
            translation.disabledByUpperLimitBtn,
            { maxCount, declinedSubs },
        ) : translation.deleteBtn;

    const InstructionLabelIcon = useCallback((props: TInstructionLink) => {
        return isInstructionsLinksEnabled
            ? <InstructionLink {...{ ...props, linkType: ELinkType.INFO_ICON }} linkRef={instructionLinkRef}/>
            : null;
    },[isInstructionsLinksEnabled]);

    const selectOptions = useMemo(() => {
        const options: IOptionItemProps[] = [
            {
                value: EPageSubscriberConstants.import,
                label: translation.importBtn,
                labelIcon: <InstructionLabelIcon {...subsImportPers}/>,
            },
            {
                value: EPageSubscriberConstants.delete,
                label: buttonLabel,
                disabled: lowerLimitDisable || upperLimitDisable,
                labelIcon: lowerLimitDisable || upperLimitDisable
                    ? undefined
                    : <InstructionLabelIcon {...subsImportPersDel}/>,
            },
        ];

        if (isImportSubscribersLimitsAvailable) {
            if (typeof translation.wizardMultiSearch === 'string')
                options.unshift(
                    {
                        value: EPageSubscriberConstants.wizardMultiSearch,
                        label: translation.wizardMultiSearch,
                        labelIcon: <InstructionLabelIcon {...subsImportSubs}/>,
                    },
                );

            if (typeof translation.wizardImportSubscribersLimits === 'string')
                options.push(
                    {
                        value: EPageSubscriberConstants.wizardImportSubscribersLimits,
                        label: translation.wizardImportSubscribersLimits,
                        labelIcon: <InstructionLabelIcon {...subsImportLimits}/>,
                    },
                );
        }

        return options;
    }, [
        translation.importBtn,
        translation.wizardMultiSearch,
        translation.wizardImportSubscribersLimits,
        InstructionLabelIcon, subsImportPers,
        buttonLabel,
        lowerLimitDisable,
        upperLimitDisable,
        subsImportPersDel,
        isImportSubscribersLimitsAvailable,
        subsImportSubs,
        subsImportLimits,
    ]);

    return (
        <PagePanelButtonSelectStyled data-name="Import personal data" iconLeft={icons.uploadIcon}>
            <Select
                instructionLinkRef={instructionLinkRef}
                kind="normal"
                options={selectOptions}
                placeholder={translation.titleBtn}
                translation={translationCommon}
                onSelect={onSelectHandler}
            />
        </PagePanelButtonSelectStyled>
    );
};

export default SubscriberMobilePersonalDataButton;
